import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { pendingCount, rejected, rejectedMedia, subscribe } from '../lib/outbox.js';
import { getMeta } from '../lib/localdb.js';
import { lastSyncedAt, sync, type SyncResult } from '../lib/sync.js';

/**
 * Sync status.
 *
 * Ambiguity about whether work is saved is what destroys trust in an offline app, so the
 * state is always explicit and always visible. A technician must never have to wonder
 * whether the hours they just logged made it to the office.
 *
 * Four distinct claims, never conflated:
 *
 *   - **À jour** — connected, queue empty. Everything is at the office.
 *   - **n en attente** — saved on this phone, not yet sent. Says so plainly.
 *   - **Hors ligne** — no signal. Work continues; nothing is lost.
 *   - **n à régler** — the server refused something. This one needs a person and outranks
 *     everything else.
 *
 * The bar is a link to the pending-work screen whenever anything is queued, because a count
 * you cannot inspect is just anxiety.
 */
export function SyncStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  const [queued, setQueued] = useState(0);
  const [failed, setFailed] = useState(0);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const refresh = async () => {
      setQueued(await pendingCount());
      setFailed((await rejected()).length + (await rejectedMedia()).length);
      setLastSync(await lastSyncedAt());
    };
    void refresh();

    const unsubscribe = subscribe(() => void refresh());
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      unsubscribe();
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const runSync = async () => {
    setSyncing(true);
    try {
      const result: SyncResult = await sync();
      setLastSync(new Date(result.at));
    } finally {
      setSyncing(false);
    }
  };

  // Anything rejected outranks everything else: it is the only state that needs a person.
  const state = failed > 0 ? 'failed' : !online ? 'offline' : queued > 0 ? 'queued' : 'clear';

  const label =
    state === 'failed'
      ? `${failed} à régler`
      : state === 'offline'
        ? queued > 0
          ? `Hors ligne · ${queued} en attente`
          : 'Hors ligne'
        : state === 'queued'
          ? syncing
            ? `Envoi de ${queued}…`
            : `${queued} en attente`
          : 'À jour';

  const body = (
    <>
      <span className={`sync__dot sync__dot--${state}`} aria-hidden="true" />
      <span className="sync__label">{label}</span>
      {state === 'clear' && lastSync && <span className="sync__time tnum">{shortTime(lastSync)}</span>}
    </>
  );

  return (
    <div className={`sync sync--${state}`} role="status" aria-live="polite">
      {queued > 0 || failed > 0 ? (
        <Link to="/envois" className="sync__body">
          {body}
        </Link>
      ) : (
        <span className="sync__body">{body}</span>
      )}

      <button
        type="button"
        className="sync__action"
        onClick={() => void runSync()}
        disabled={syncing || !online}
        aria-label="Synchroniser maintenant"
      >
        {syncing ? <Spinner /> : <RefreshGlyph />}
      </button>
    </div>
  );
}

/**
 * Storage warning.
 *
 * Shown only when the browser refused persistent storage, because that is the one condition
 * under which unsent work could genuinely disappear. Saying nothing would be dishonest;
 * saying it constantly would be noise, so it appears once and can be dismissed for the
 * session.
 */
export function StoragePersistenceWarning() {
  const [granted, setGranted] = useState<boolean | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    void getMeta('storagePersisted').then((value) => setGranted(value === null ? null : value === 'true'));
  }, []);

  if (granted !== false || dismissed) return null;

  return (
    <div className="warning" role="alert">
      <p>
        Ce navigateur pourrait effacer le travail non envoyé. Installez l’application sur l’écran d’accueil pour
        éviter toute perte.
      </p>
      <button type="button" onClick={() => setDismissed(true)}>
        Compris
      </button>
    </div>
  );
}

/** Short local time, so "à jour" carries a verifiable moment rather than a vague claim. */
function shortTime(date: Date): string {
  return date.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' });
}

function RefreshGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M13.5 8a5.5 5.5 0 1 1-1.7-4" />
      <path d="M13.5 2v3.2h-3.2" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg width="18" height="18" viewBox="0 0 16 16" className="spin" aria-hidden="true">
      <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
      <path d="M8 2a6 6 0 0 1 6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
